import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { DashboardCard, GraphVariable } from '../../../class/Preference';
import { AccountStateService } from '../../../services/account-state.service';
import { TripStatsService } from '../../../services/tripstats.service';
import UnitsService from '../../../services/units.service';
import { TreeComponent } from '../../tree/tree.component';
@Component({
  selector: 'chart-config',
  templateUrl: './chart-config.component.html',
  styleUrls: ['./chart-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    TranslatePipe,
    MatIcon,
    TreeComponent,
    MatTooltip,
    MatSlideToggle,
    FormsModule,
  ],
})
export class ChartConfigComponent {
  private tripStatsService = inject(TripStatsService);
  private _unitsService = inject(UnitsService);
  private _accountStateService = inject(AccountStateService);
  public $account = this._accountStateService.$account;
  public $unitsById = this._unitsService.$unitsById;

  @Output() unitEmit = new EventEmitter<string>();
  @Output() updateConfigEmit = new EventEmitter<DashboardCard>();
  @Output() updateCompareEmit = new EventEmitter<boolean>();
  @Output() periodEmit = new EventEmitter<DashboardCard>();
  @Output() resetServiceEmit = new EventEmitter<boolean>();
  @Output() changeServiceEmit = new EventEmitter<number>();

  @Input() cameFromService: boolean;
  @Input() index: number;

  public $datas = this.tripStatsService.$config;
  public unit = 'h';
  public changingService = false;

  public setColor(type: string, opacity: number): string {
    switch (type) {
      case 'mileage':
        return 'rgba(46, 170, 221, ' + opacity.toString() + ')';
      case 'stops':
        return 'rgba(249, 53, 40, ' + opacity.toString() + ')';
      case 'engine':
        return 'rgba(249, 184, 41, ' + opacity.toString() + ')';
      case 'trip_time':
        return 'rgba(249, 134, 40, ' + opacity.toString() + ')';
      case 'speed_avg':
        return 'rgba(45, 45, 221, ' + opacity.toString() + ')';
      case 'eco_avg':
        return 'rgba(167, 219, 138, ' + opacity.toString() + ')';
      case 'fuel_quantity_100_km':
        return 'rgba(139, 45, 221, ' + opacity.toString() + ')';
      case 'fuel_cost_100_km':
        return 'rgba(45, 221, 171, ' + opacity.toString() + ')';
      case 'CO2_100_km':
        return 'rgba(57, 221, 45, ' + opacity.toString() + ')';
    }
  }

  public updateData(value: GraphVariable) {
    this.$datas.update(d => ({
      ...d,
      y: value,
    }));
    this.updateConfig();
  }

  public updateGroup(value: string) {
    this.$datas.update(d => ({
      ...d,
      group: value,
    }));
    this.updateConfig();
  }

  private updateConfig() {
    this.tripStatsService.$config.set(this.$datas());
    this.updateConfigEmit.emit(this.$datas());
  }

  public updateUnit() {
    this.unitEmit.emit(this.unit);
  }

  public setPeriod(value: string) {
    this.$datas.update(d => ({
      ...d,
      period: value,
    }));
    this.tripStatsService.$config.set(this.$datas());
    this.periodEmit.emit(this.$datas());
  }

  public selectService() {
    this.changingService = !this.changingService;
  }

  public resetServices() {
    this.changingService = false;
    this.$datas.update(d => ({
      ...d,
      uid_service: 0,
    }));
    this.tripStatsService.$config.set(this.$datas());
    this.resetServiceEmit.emit(true);
  }

  public changeService(uid: number) {
    if (uid !== this.$datas().uid_service) {
      if (uid === 0) {
        this.resetServices();
      } else {
        this.$datas.update(d => ({
          ...d,
          uid_service: uid,
        }));
        this.tripStatsService.$config.set(this.$datas());
        this.changingService = false;
        this.changeServiceEmit.emit(uid);
      }
    }
  }

  public navigateServiceUp() {
    if (this.$datas().group === 'device') {
      this.$datas.update(d => ({
        ...d,
        group: 'service',
      }));
      this.changeService(this.$datas().uid_service);
    } else {
      const parentId = this._unitsService.$parentById()?.[this.$datas().uid_service];
      if (!parentId) {
        this.$datas.update(d => ({
          ...d,
          uid_service: undefined,
        }));
        this.updateConfig();
      } else {
        this.changeService(parentId);
      }
    }
    this.cameFromService = false;
  }

  public updateCompare(value: boolean) {
    this.$datas.update(d => ({
      ...d,
      compare: value,
    }));
    this.tripStatsService.$config.set(this.$datas());
    this.updateCompareEmit.emit(this.$datas().compare);
  }
}
